import { Box, Separator, Stack } from "@chakra-ui/react"
import { motion } from "framer-motion"
import React, { useEffect, useMemo, useState } from "react"
import type {
  AugmentationToolbarOption,
  AugmentationToolbarProps
} from "./augmentationToolbarTypes"
import { AugmentationToolbarItem } from "./AugmentationToolbarButton"
import { canRenderToolbarOption } from "./augmentationToolbarVisibility"

/**
 * Compact floating toolbar: island capsule for non-primary groups, primary
 * actions adjacent outside. Detailed mode is a labeled list (popover / menu).
 * All visible options always render (no expand-on-hover). Icons stagger in on
 * enter; the parent does not animate its size. The island clips overflow until
 * that enter finishes so sliding icons don't paint outside the capsule.
 * Primary actions sit outside the island and must not be clipped — their
 * circular shadows would otherwise flatten into a rectangle.
 */
const AugmentationToolbarView = React.forwardRef<
  HTMLDivElement,
  AugmentationToolbarProps
>(function AugmentationToolbarView(
  { toolbar, alwaysVisible, direction, detailed, yieldsToPanel: _, ...stackProps },
  ref
) {
  direction ||= "column"
  // Only permitting detailed (labeled) lists in column mode.
  detailed = direction === "column" && !!detailed
  const { options, structuredOptions, selectedKey } = toolbar

  // Clip entering icons to the toolbar bounds; restore overflow after stagger.
  const [isAnimating, setIsAnimating] = useState(true)

  const canRenderOption = (option: AugmentationToolbarOption) =>
    canRenderToolbarOption(option, { detailed })

  const visibleOptions = options.filter(canRenderOption)

  // Remount when the visible option set changes so stagger runs again
  // (e.g. side ↔ bottom toolbar).
  const optionsKey =
    visibleOptions.map((option) => option.key).join(",") + `${detailed}`

  useEffect(() => {
    setIsAnimating(true)
  }, [optionsKey])

  // Animate options in from the relative side; parent only orchestrates stagger.
  const childDynamicDirKey = direction === "column" ? "x" : "y"
  const totalDuration = 0.2
  const childDuration = 0.3
  const stagger =
    visibleOptions.length > 1
      ? (totalDuration - childDuration) / (visibleOptions.length - 1)
      : 0

  const parentVariants = useMemo(
    () => ({
      collapsed: {},
      expanded: {
        transition: {
          staggerChildren: -stagger
        }
      }
    }),
    [stagger]
  )

  const childVariants = useMemo(
    () => ({
      // Detailed toolbars are panel lists. Their rows can fade with the surface,
      // but should not fly in as though they were floating toolbar buttons.
      collapsed: { opacity: 0, [childDynamicDirKey]: detailed ? 0 : -50 },
      expanded: {
        opacity: 1,
        [childDynamicDirKey]: 0,
        transition: { duration: childDuration }
      }
    }),
    [childDynamicDirKey, detailed]
  )

  const overflowWhileAnimating = isAnimating ? "hidden" : "visible"

  // Group the toolbar options into palettes.
  const paletteGroups = (
    structuredOptions.some(Array.isArray)
      ? structuredOptions.map((optionGroup) =>
          Array.isArray(optionGroup) ? optionGroup : [optionGroup]
        )
      : [structuredOptions]
  ) as AugmentationToolbarOption[][]

  const visiblePaletteGroups = paletteGroups
    .map((paletteGroup) => paletteGroup.filter(canRenderOption))
    .filter((paletteGroup) => paletteGroup.length > 0)

  const hasVisibleOptions = visiblePaletteGroups.length > 0

  // Compact mode wraps non-primary groups in one frosted island so primary
  // actions (e.g. Add Path) sit adjacent outside the capsule.
  const islandGroups = detailed
    ? []
    : visiblePaletteGroups.filter(
        (group) => !group.some((option) => option.isPrimary)
      )
  const primaryGroups = detailed
    ? []
    : visiblePaletteGroups.filter((group) =>
        group.some((option) => option.isPrimary)
      )
  const detailedGroups = detailed ? visiblePaletteGroups : []

  function renderOptionGroup(
    optionGroup: AugmentationToolbarOption[],
    {
      showDetailedSeparator,
      islandGroupIndex
    }: { showDetailedSeparator?: boolean; islandGroupIndex?: number } = {}
  ) {
    const hasPrimary = optionGroup.some((o) => o.isPrimary)
    const useIslandChrome = !detailed && !hasPrimary
    const isColumn = direction === "column"
    const showIslandSeparator =
      useIslandChrome && islandGroupIndex !== undefined && islandGroupIndex > 0

    return (
      <Stack
        key={optionGroup.map((o) => o.key).join("-")}
        className="AugmentationToolbarGroup"
        direction={direction}
        alignItems={useIslandChrome ? "flex-start" : undefined}
        alignSelf="center"
        backdropFilter={
          detailed || hasPrimary || useIslandChrome ? "none" : "blur(10px)"
        }
        bg={
          detailed || hasPrimary || useIslandChrome
            ? "transparent"
            : "whiteAlpha.600"
        }
        borderRadius={useIslandChrome ? 0 : "card"}
        borderColor={
          showIslandSeparator ? "augmentationPanel.border" : undefined
        }
        borderStyle={showIslandSeparator ? "solid" : undefined}
        borderTopWidth={showIslandSeparator && isColumn ? "1px" : undefined}
        borderLeftWidth={showIslandSeparator && !isColumn ? "1px" : undefined}
        flexShrink={direction === "row" ? 0 : undefined}
        gap={useIslandChrome ? "5px" : 2}
        overflow={hasPrimary ? "visible" : undefined}
        p={
          useIslandChrome
            ? isColumn
              ? "5px 0"
              : "0 5px"
            : hasPrimary
            ? 0
            : ".125em"
        }
        w={
          useIslandChrome
            ? isColumn
              ? "38px"
              : "auto"
            : direction === "row"
            ? "auto"
            : "full"
        }
        _hover={{
          bg:
            detailed || hasPrimary || useIslandChrome
              ? "transparent"
              : "whiteAlpha.700"
        }}
      >
        {showDetailedSeparator ? (
          <Separator
            m={0}
            borderBottomWidth={0}
            borderTopWidth="1px"
            w="full"
          />
        ) : undefined}
        <Stack
          direction={direction}
          alignItems={direction === "row" ? "center" : "stretch"}
          gap={useIslandChrome ? "5px" : 1}
          className="ToolbarStack"
          flexShrink={direction === "row" ? 0 : undefined}
          position="relative"
        >
          {optionGroup.map((option) => (
            <Box
              asChild
              className="AugmentationToolbarItem"
              key={option.key}
              pos="relative"
            >
              <motion.div variants={childVariants}>
                <AugmentationToolbarItem
                  option={visibleOptions.find((o) => o.key === option.key)!}
                  isSelected={selectedKey === option.key}
                  detailed={detailed}
                />
              </motion.div>
            </Box>
          ))}
        </Stack>
      </Stack>
    )
  }

  if (!(alwaysVisible || !selectedKey) || !hasVisibleOptions) {
    return null
  }

  return (
    <motion.div
      key={optionsKey}
      variants={parentVariants}
      initial="collapsed"
      animate="expanded"
      onAnimationComplete={() => setIsAnimating(false)}
      // Row toolbars sit in a maxW host that scrolls. Size to content so the
      // host sees overflow — do not animate parent width/height.
      style={
        direction === "row"
          ? { width: "max-content", maxWidth: "none" }
          : undefined
      }
    >
      <Stack
        ref={ref}
        className="AugmentationToolbar"
        gap={2}
        direction={direction}
        borderRadius="card"
        overflow="visible"
        bg={detailed ? "whiteAlpha.600" : "transparent"}
        backdropFilter={detailed ? "blur(10px)" : "none"}
        {...(direction === "row"
          ? { flexShrink: 0, w: "max-content", maxW: "none" }
          : {})}
        {...stackProps}
      >
        {detailed ? (
          detailedGroups.map((optionGroup, i) =>
            renderOptionGroup(optionGroup, {
              showDetailedSeparator: i > 0
            })
          )
        ) : (
          <>
            {islandGroups.length > 0 ? (
              <Stack
                className="AugmentationToolbarIsland"
                alignItems="flex-start"
                backdropFilter="blur(5px)"
                bg="augmentationPanel.toolbarSurface"
                borderRadius="augmentationPanel"
                boxShadow="augmentationPanel"
                direction={direction}
                flexShrink={direction === "row" ? 0 : undefined}
                gap={0}
                overflow={overflowWhileAnimating}
                p={direction === "column" ? "0 5px" : "5px 0"}
              >
                {islandGroups.map((optionGroup, islandGroupIndex) =>
                  renderOptionGroup(optionGroup, { islandGroupIndex })
                )}
              </Stack>
            ) : null}
            {primaryGroups.map((optionGroup) => renderOptionGroup(optionGroup))}
          </>
        )}
      </Stack>
    </motion.div>
  )
})

export default AugmentationToolbarView
